import { forwardRef } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';

/**
 * Surface / Card primitive — a token-driven container. Use for any panel that
 * needs a distinct surface (cards, stat tiles, widget shells).
 *
 * Contract:
 * - `variant`: 'elevated' | 'outline' | 'subtle' (elevated is flat per ENG-2145)
 * - All color/border/shadow values come from semantic tokens.
 */
export type CardVariant = 'elevated' | 'outline' | 'subtle';

export interface CardProps extends BoxProps {
	variant?: CardVariant;
}

const VARIANT_STYLES: Record<CardVariant, BoxProps> = {
	// Flat platform (ENG-2145): 'elevated' keeps its name for call-site
	// compatibility but renders flat — borders carry the separation.
	elevated: {
		bg: 'bg.surface',
		borderWidth: '1px',
		borderColor: 'border.default',
		boxShadow: 'none',
	},
	outline: {
		bg: 'bg.surface',
		borderWidth: '1px',
		borderColor: 'border.default',
	},
	subtle: {
		bg: 'bg.subtle',
		borderWidth: '1px',
		borderColor: 'transparent',
	},
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
	({ variant = 'elevated', children, ...rest }, ref) => (
		<Box ref={ref} borderRadius="xl" p={4} {...VARIANT_STYLES[variant]} {...rest}>
			{children}
		</Box>
	),
);
Card.displayName = 'Card';

/** Alias for non-card surfaces (toolbars, sections) that want the same tokens. */
export const Surface = Card;

export default Card;
